import { Component, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { TranslatePipe } from '@ngx-translate/core';
import { HlmInput } from '@spartan-ng/helm/input';
import { BrnDialogRef, injectBrnDialogContext } from '@spartan-ng/brain/dialog';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmSpinner } from '@spartan-ng/helm/spinner';
import { ThemedIconComponent } from '../../atoms/themed-icon/themed-icon.component';

export interface UploadResult {
  modelName: string;
  description: string;
}

export interface UploadFormData {
  onNext: Subject<UploadResult>;
  onReset?: Subject<void>;
}

@Component({
  selector: 'ocp-upload-form',
  templateUrl: './upload-form.component.html',
  styleUrl: './upload-form.component.scss',
  imports: [
    ReactiveFormsModule,
    HlmButtonImports,
    HlmSpinner,
    HlmInput,
    TranslatePipe,
    ThemedIconComponent,
  ],
})
export class UploadFormComponent {
  private readonly dialogRef = inject<BrnDialogRef<unknown>>(BrnDialogRef, { optional: true });
  private readonly data = injectBrnDialogContext<UploadFormData>({ optional: true });

  protected readonly waiting = signal(false);

  protected readonly form = new FormGroup({
    modelName: new FormControl('', {
      nonNullable: true,
      validators: [
        Validators.required,
        control => (control.value.trim() ? null : { required: true }),
      ],
    }),
    description: new FormControl('', { nonNullable: true }),
  });

  constructor() {
    this.data?.onReset?.pipe(takeUntilDestroyed()).subscribe(() => {
      this.waiting.set(false);
      this.form.enable();
    });
  }

  protected onNext(): void {
    if (this.form.invalid) return;
    const onNext = this.data?.onNext;
    if (!onNext) return;
    const result: UploadResult = {
      modelName: this.form.controls.modelName.value.trim(),
      description: this.form.controls.description.value.trim(),
    };
    this.waiting.set(true);
    this.form.disable();
    onNext.next(result);
  }

  protected onCancel(): void {
    this.dialogRef?.close();
  }
}
